import { useState } from 'react'

import { Button } from '@factorialco/deprecated-design-system'
import {
  AtsAddJobPostingSettingsCustomQuestionDocument,
  AtsCreateQuestionTemplateDocument,
  AtsGetJobPostingSettingsApplicationFormDocument,
  AtsGetQuestionTemplatesFeatureDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { CreateQuestionForm } from 'modules/ats/components/Questions/CreateQuestionForm'
import { CreateQuestionFormValues } from 'modules/ats/components/Questions/QuestionFormValues'
import { questionTypeHasOptions } from 'modules/ats/lib/utils/customQuestion'
import { validateQuestions } from 'modules/ats/lib/utils/questions_validations'

interface Props {
  position: number
  jobId: string
  companyId: string
  onClose: () => void
}

export const AddCustomQuestion = ({ onClose, position, jobId, companyId }: Props) => {
  const { data: feature } = useQuery(AtsGetQuestionTemplatesFeatureDocument)
  const questionTemplateEnabled =
    feature?.companies.featureAvailabilitiesConnection.nodes.find(
      (node) => node.id === 'ats_customized_features'
    )?.available ?? false

  const [addCustomQuestion] = useMutation(AtsAddJobPostingSettingsCustomQuestionDocument, {
    extractorFn: (data) => data.atsMutations.createAtsQuestion,
    refetchQueries: [AtsGetJobPostingSettingsApplicationFormDocument],
  })

  const [createQuestionTemplate] = useMutation(AtsCreateQuestionTemplateDocument, {
    extractorFn: (data) => data.atsMutations.createAtsQuestionTemplate,
  })

  const [saveAsTemplate, setSaveAsTemplate] = useState(false)

  const form = useForm<CreateQuestionFormValues>({
    defaultValues: {
      label: '',
      mandatory: false,
      autoDisqualify: false,
      questionType: 'long_text',
      options: [],
    },
    validate(data) {
      return validateQuestions(data, {})
    },
    async onSubmit(values, { setErrors }) {
      const shouldSendOptions = questionTypeHasOptions(values.questionType)

      track(trackingEvents.ats.jobSettingsAddApplicationQuestion)

      const result = await addCustomQuestion({
        variables: {
          companyId: companyId,
          atsJobPostingId: jobId,
          position: position,
          options: shouldSendOptions ? values.options : null,
          autoDisqualify: values.autoDisqualify,
          label: values.label,
          mandatory: values.mandatory,
          questionType: values.questionType ?? 'long_text',
        },
      })

      const handled = handleResult(result, ({ label, mandatory, questionType }) => {
        setErrors({
          label,
          mandatory,
          questionType,
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })

        if (saveAsTemplate) {
          await submitTemplateForm(values)
        }

        onClose()
      }

      return handled
    },
  })

  const templateForm = useForm<CreateQuestionFormValues>({
    defaultValues: {
      label: '',
      mandatory: false,
      autoDisqualify: false,
      questionType: 'long_text',
      options: [],
    },
    validate(data) {
      return validateQuestions(data, {})
    },
    async onSubmit(values, { setErrors }) {
      const shouldSendOptions = questionTypeHasOptions(values.questionType)

      const result = await createQuestionTemplate({
        variables: {
          companyId: companyId,
          options: shouldSendOptions ? values.options : null,
          autoDisqualify: values.autoDisqualify,
          label: values.label,
          mandatory: values.mandatory,
          questionType: values.questionType,
        },
      })

      const handled = handleResult(result, ({ label, mandatory, questionType }) => {
        setErrors({
          label,
          mandatory,
          questionType,
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
        })
      }

      return handled
    },
  })

  const submitTemplateForm = async (values: CreateQuestionFormValues) => {
    templateForm.setValues({
      label: values.label,
      mandatory: values.mandatory,
      autoDisqualify: values.autoDisqualify,
      questionType: values.questionType,
      options: values.options,
    })

    await createQuestionTemplate({
      variables: {
        companyId: companyId,
        options: questionTypeHasOptions(values.questionType) ? values.options : null,
        autoDisqualify: values.autoDisqualify,
        label: values.label,
        mandatory: values.mandatory,
        questionType: values.questionType,
      },
    })
  }

  return (
    <Modal
      illustration="server-edit"
      title={i18n.t('ats.job_posting_settings.application_form.custom_questions.add_modal_title')}
      description={i18n.t(
        'ats.job_posting_settings.application_form.custom_questions.add_modal_description'
      )}
      onClose={onClose}
      onSubmit={() => {
        form.submitForm()
      }}
      footer={
        <Button disabled={form.isSubmitting} submit>
          {i18n.t('common.save')}
        </Button>
      }
    >
      <CreateQuestionForm
        form={form}
        templateFeatureEnabled={questionTemplateEnabled}
        saveAsTemplate={saveAsTemplate}
        setSaveAsTemplate={setSaveAsTemplate}
      />
    </Modal>
  )
}
